import { useEffect, useRef, useState } from "preact/hooks";
import { api } from "./api";
import { getEffectiveRealtimeEndDate } from "./dateRange";
import { appendDashboardFiltersToSearchParams, dashboardFilters, dateRangePreset, endDate, isDashboardFilterActive, selectedProperty, startDate, toggleDashboardFilter } from "./state";
import { subscribeToAggregateRealtime } from "./realtime";
import type { RealtimeConnectionStatus } from "./realtime";

type TopSource = {
  source: string;
  visits: number;
};

type AggregateOverviewResponse = {
  pageviews: number;
  consentRefused: number;
  consentRevoked: number;
  anonymousEvents: number;
  pseudonymousEvents: number;
  uniquePages: number;
  topSources: TopSource[];
};

const INITIAL_DATA: AggregateOverviewResponse = {
  pageviews: 0,
  consentRefused: 0,
  consentRevoked: 0,
  anonymousEvents: 0,
  pseudonymousEvents: 0,
  uniquePages: 0,
  topSources: [],
};

type AggregatedOverviewProps = {
  liveRefreshTick?: number;
};

const AggregatedOverview = ({
  liveRefreshTick = 0,
}: AggregatedOverviewProps) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<AggregateOverviewResponse>(INITIAL_DATA);
  const [realtimeStatus, setRealtimeStatus] = useState<RealtimeConnectionStatus>("disconnected");
  const refreshAggregateRef = useRef<(showLoading?: boolean) => void>(() => {});

  useEffect(() => {
    if (!selectedProperty.value) return;
    let cancelled = false;

    const loadAggregateOverview = async (showLoading = true) => {
      if (showLoading) {
        setLoading(true);
      }

      try {
        const effectiveEndDate = getEffectiveRealtimeEndDate(endDate.value);
        const params = new URLSearchParams({
          property: selectedProperty.value,
          start: startDate.value,
          end: effectiveEndDate,
        });
        appendDashboardFiltersToSearchParams(params);
        const result = await api(`/api/aggregate-overview?${params.toString()}`);

        if (cancelled) {
          return;
        }

        setData({
          pageviews: result.pageviews ?? 0,
          consentRefused: result.consentRefused ?? 0,
          consentRevoked: result.consentRevoked ?? 0,
          anonymousEvents: result.anonymousEvents ?? 0,
          pseudonymousEvents: result.pseudonymousEvents ?? 0,
          uniquePages: result.uniquePages ?? 0,
          topSources: Array.isArray(result.topSources) ? result.topSources : [],
        });
      } catch (error) {
        if (!cancelled) {
          console.error("Error fetching aggregated overview:", error);
          setData(INITIAL_DATA);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    refreshAggregateRef.current = (showLoading = false) => {
      void loadAggregateOverview(showLoading);
    };

    let refreshTimer: number | null = null;
    const scheduleRefresh = () => {
      if (refreshTimer !== null) {
        return;
      }

      refreshTimer = window.setTimeout(() => {
        refreshTimer = null;
        void loadAggregateOverview(false);
      }, 500);
    };

    void loadAggregateOverview(true);
    const unsubscribe = subscribeToAggregateRealtime(
      selectedProperty.value,
      scheduleRefresh,
      setRealtimeStatus
    );

    return () => {
      cancelled = true;
      if (refreshTimer !== null) {
        window.clearTimeout(refreshTimer);
      }
      setRealtimeStatus("disconnected");
      unsubscribe();
    };
  }, [selectedProperty.value, startDate.value, endDate.value, JSON.stringify(dashboardFilters.value)]);

  useEffect(() => {
    if (!selectedProperty.value) return;
    if (dateRangePreset.value !== "live") return;
    if (realtimeStatus === "connected") return;

    const pollId = window.setInterval(() => {
      refreshAggregateRef.current(false);
    }, 5000);

    return () => {
      window.clearInterval(pollId);
    };
  }, [realtimeStatus, selectedProperty.value, startDate.value, endDate.value, dateRangePreset.value, JSON.stringify(dashboardFilters.value)]);

  useEffect(() => {
    if (!selectedProperty.value) return;
    if (dateRangePreset.value !== "live") return;
    if (liveRefreshTick === 0) return;

    refreshAggregateRef.current(false);
  }, [liveRefreshTick, selectedProperty.value, startDate.value, endDate.value, JSON.stringify(dashboardFilters.value)]);

  const realtimeLabel = realtimeStatus === "connected" ? "Live" : "Offline";

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title">Dati aggregati anonimi</h3>
        <div className={`realtime-indicator realtime-indicator-${realtimeStatus === "connected" ? "connected" : "disconnected"}`}>
          <span className="realtime-indicator-dot" />
          <span>{realtimeLabel}</span>
        </div>
      </div>

      <div className="stats-row aggregate-stats-row">
        <div className="stat-item">
          <div className="stat-label">Pageview</div>
          <div className="stat-value">{loading ? "-" : data.pageviews.toLocaleString()}</div>
        </div>
        <div className="stat-item">
          <div className="stat-label">Pagine uniche</div>
          <div className="stat-value">{loading ? "-" : data.uniquePages.toLocaleString()}</div>
        </div>
        <div className="stat-item">
          <div className="stat-label">Rifiuti consenso</div>
          <div className="stat-value">{loading ? "-" : data.consentRefused.toLocaleString()}</div>
        </div>
        <div className="stat-item">
          <div className="stat-label">Revoche consenso</div>
          <div className="stat-value">{loading ? "-" : data.consentRevoked.toLocaleString()}</div>
        </div>
      </div>

      {loading ? (
        <div className="loading">
          <div className="loading-spinner"></div>
        </div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Sorgente UTM</th>
              <th className="table-cell-number">Pageview</th>
            </tr>
          </thead>
          <tbody>
            {data.topSources.length === 0 ? (
              <tr>
                <td className="table-cell-secondary" colSpan={2}>
                  Nessuna sorgente disponibile nel periodo selezionato
                </td>
              </tr>
            ) : (
              data.topSources.map((source) => (
                <tr
                  key={source.source}
                  className={`filterable-row ${isDashboardFilterActive('utm_source', source.source) ? 'is-filter-active' : ''}`}
                  onClick={() => toggleDashboardFilter('utm_source', source.source)}
                >
                  <td className="table-cell-primary">{source.source}</td>
                  <td className="table-cell-number">{source.visits}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}

      <p className="section-note" style={{ marginTop: "12px" }}>
        Eventi anonimi: {loading ? "-" : data.anonymousEvents.toLocaleString()} · Eventi
        pseudonimi: {loading ? "-" : data.pseudonymousEvents.toLocaleString()}
      </p>
    </div>
  );
};

export default AggregatedOverview;
